import { createFileRoute, Link, useLocation } from "@tanstack/react-router";
import * as React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { requireAuth } from "~/lib/auth-guard";
import { orpc } from "~/lib/orpc-client";
import { Skeleton, useToast } from "@alchema/ui";
import * as m from "~/paraglide/messages";
import { trackEvent } from "~/lib/posthog-client";

export const Route = createFileRoute("/jobs/search-criteria")({
  beforeLoad: async () => { await requireAuth(); },
  component: SearchCriteriaPage,
});

const SOURCES = ["linkedin", "indeed", "stepstone", "xing"] as const;
const WORK_TYPES = ["any", "remote", "hybrid", "onsite"] as const;

function JobTabs() {
  const location = useLocation();
  const { data: newCount } = useQuery({
    ...orpc.jobSearch.newJobCount.queryOptions(),
    staleTime: 60_000,
    retry: 0,
  });

  const tabs = [
    { label: m.jobs_tab_my_jobs(), to: "/jobs" },
    { label: m.jobs_tab_scraped(), to: "/jobs/scraped", badge: newCount?.count },
    { label: m.jobs_tab_search_criteria(), to: "/jobs/search-criteria" },
  ];

  return (
    <div className="mb-6 flex gap-1 rounded-lg bg-[var(--bg-secondary)] p-1">
      {tabs.map((tab) => {
        const isActive = location.pathname === tab.to;
        return (
          <Link
            key={tab.to}
            to={tab.to}
            className={`relative rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              isActive ? "bg-[var(--surface)] text-[var(--text)] shadow-[var(--shadow-sm)]" : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
            }`}
          >
            {tab.label}
            {tab.badge && tab.badge > 0 ? (
              <span className="ml-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-status-error px-1.5 text-[10px] font-bold text-white">
                {tab.badge}
              </span>
            ) : null}
          </Link>
        );
      })}
    </div>
  );
}

interface CriteriaFormData {
  name: string;
  role: string;
  location: string;
  salaryMin: string;
  salaryMax: string;
  currency: string;
  workType: string;
  sources: string[];
}

const emptyForm: CriteriaFormData = {
  name: "",
  role: "",
  location: "",
  salaryMin: "",
  salaryMax: "",
  currency: "EUR",
  workType: "any",
  sources: ["linkedin"],
};

function SearchCriteriaPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<CriteriaFormData>(emptyForm);
  const [formError, setFormError] = React.useState("");

  const criteriaQuery = useQuery({
    ...orpc.jobSearch.listCriteria.queryOptions(),
    staleTime: 60_000,
  });
  const criteria = criteriaQuery.data ?? [];

  const { data: tierData } = useQuery({
    ...orpc.billing.currentTier.queryOptions(),
    staleTime: 300_000,
    retry: 0,
  });
  const tierLoaded = tierData !== undefined;
  const isFree = tierLoaded && tierData.tier === "free";

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: orpc.jobSearch.listCriteria.key({}) });
  };

  const createMutation = useMutation(
    orpc.jobSearch.createCriteria.mutationOptions({
      onSuccess: () => {
        invalidate();
        setShowForm(false);
        setForm(emptyForm);
        setFormError("");
        toast(m.jobs_criteria_created(), "success");
      },
      onError: (err) => {
        const msg = err.message || "Failed to create";
        setFormError(msg);
        toast(msg, "error");
      },
    }),
  );

  const updateMutation = useMutation(
    orpc.jobSearch.updateCriteria.mutationOptions({
      onSuccess: () => {
        invalidate();
        setEditingId(null);
        setForm(emptyForm);
        toast(m.jobs_criteria_updated(), "success");
      },
      onError: (err) => toast(err.message || "Failed to update", "error"),
    }),
  );

  const deleteMutation = useMutation(
    orpc.jobSearch.deleteCriteria.mutationOptions({
      onSuccess: () => {
        invalidate();
        toast(m.jobs_criteria_deleted(), "success");
      },
      onError: (err) => toast(err.message || "Failed to delete", "error"),
    }),
  );

  const scrapeMutation = useMutation(
    orpc.jobSearch.scrapeNow.mutationOptions({
      onSuccess: (data) => {
        invalidate();
        queryClient.invalidateQueries({ queryKey: orpc.jobSearch.listScrapedJobs.key({}) });
        queryClient.invalidateQueries({ queryKey: orpc.jobSearch.newJobCount.key({}) });
        toast(m.jobs_scraped_count({ added: data.added, total: data.total }), "success");
      },
      onError: (err) => toast(err.message || "Scrape failed", "error"),
    }),
  );

  const toggleActiveMutation = useMutation(
    orpc.jobSearch.updateCriteria.mutationOptions({
      onSuccess: () => invalidate(),
      onError: (err) => toast(err.message || "Failed to toggle", "error"),
    }),
  );

  const alertMutation = useMutation(
    orpc.jobSearch.alertSettings.mutationOptions({
      onSuccess: () => {
        invalidate();
        toast("Alert settings updated", "success");
      },
      onError: (err) => toast(err.message || "Failed to update alerts", "error"),
    }),
  );

  function handleSourceToggle(source: string) {
    setForm((f) => ({
      ...f,
      sources: f.sources.includes(source)
        ? f.sources.filter((s) => s !== source)
        : [...f.sources, source],
    }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload = {
      name: form.name,
      role: form.role,
      ...(form.location ? { location: form.location } : {}),
      ...(form.salaryMin ? { salaryMin: Number(form.salaryMin) } : {}),
      ...(form.salaryMax ? { salaryMax: Number(form.salaryMax) } : {}),
      currency: form.currency,
      workType: form.workType as "remote" | "hybrid" | "onsite" | "any",
      sources: form.sources as ("linkedin" | "indeed" | "stepstone" | "xing")[],
    };

    trackEvent("job_searched", {
      has_query: Boolean(form.role.trim()),
      has_filters: Boolean(form.location || form.workType || form.sources.length),
    });

    setFormError("");
    if (editingId) {
      updateMutation.mutate({ id: editingId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  function startEdit(c: typeof criteria[0]) {
    setEditingId(c.id);
    setForm({
      name: c.name,
      role: c.role,
      location: c.location ?? "",
      salaryMin: c.salaryMin?.toString() ?? "",
      salaryMax: c.salaryMax?.toString() ?? "",
      currency: c.currency,
      workType: c.workType,
      sources: c.sources,
    });
    setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  }

  return (
    <div className="mx-auto max-w-4xl p-8">
      <JobTabs />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{m.jobs_search_criteria()}</h1>
          <p className="mt-1 text-sm text-[var(--text-muted)]">{m.jobs_manage_desc()}</p>
        </div>
        {!showForm && (
          isFree ? (
            <Link
              to="/pricing"
              className="rounded-lg bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white hover:opacity-90"
            >
              {m.settings_upgrade_pro()}
            </Link>
          ) : (
            <button
              onClick={() => { setShowForm(true); setEditingId(null); setForm(emptyForm); }}
              className="rounded-lg bg-[var(--text)] px-4 py-2 text-sm font-medium text-[var(--text-inverse)] hover:opacity-90"
            >
              {m.jobs_add_criteria()}
            </button>
          )
        )}
      </div>

      {/* Create/Edit form */}
      {showForm && (
        <form onSubmit={handleSubmit} className="mt-6 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 shadow-[var(--shadow-sm)]">
          <h2 className="mb-4 text-lg font-semibold">{editingId ? m.jobs_edit_criteria_title() : m.jobs_new_criteria_title()}</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">{m.jobs_search_criteria_label_name()}</label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm"
                placeholder={m.jobs_placeholder_search_name()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">{m.jobs_search_criteria_label_role()}</label>
              <input
                type="text"
                required
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm"
                placeholder={m.jobs_placeholder_role()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">{m.jobs_search_criteria_label_location()}</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm"
                placeholder={m.jobs_placeholder_location()}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">{m.jobs_search_criteria_label_work_type()}</label>
              <select
                value={form.workType}
                onChange={(e) => setForm((f) => ({ ...f, workType: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm"
              >
                {WORK_TYPES.map((wt) => (
                  <option key={wt} value={wt}>{wt.charAt(0).toUpperCase() + wt.slice(1)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">{m.jobs_search_criteria_label_salary_min()}</label>
              <input
                type="number"
                value={form.salaryMin}
                onChange={(e) => setForm((f) => ({ ...f, salaryMin: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm"
                placeholder={m.jobs_placeholder_salary({ amount: "60000" })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[var(--text-secondary)]">{m.jobs_search_criteria_label_salary_max()}</label>
              <input
                type="number"
                value={form.salaryMax}
                onChange={(e) => setForm((f) => ({ ...f, salaryMax: e.target.value }))}
                className="mt-1 w-full rounded-lg border border-[var(--border)] px-3 py-2 text-sm"
                placeholder={m.jobs_placeholder_salary({ amount: "90000" })}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-[var(--text-secondary)]">{m.jobs_search_criteria_label_sources()}</label>
            <div className="mt-2 flex gap-3">
              {SOURCES.map((src) => (
                <label key={src} className="flex items-center gap-1.5 text-sm text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={form.sources.includes(src)}
                    onChange={() => handleSourceToggle(src)}
                    className="rounded border-[var(--border)]"
                  />
                  {src.charAt(0).toUpperCase() + src.slice(1)}
                </label>
              ))}
            </div>
          </div>

          {formError && (
            <div className="mt-4 rounded-lg border border-[var(--error)] bg-[var(--error-subtle)] px-4 py-3 text-sm text-[var(--error)]">
              {formError}
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              disabled={createMutation.isPending || updateMutation.isPending}
              className="rounded-lg bg-[var(--text)] px-4 py-2 text-sm font-medium text-[var(--text-inverse)] hover:opacity-90 disabled:opacity-50"
            >
              {(createMutation.isPending || updateMutation.isPending) ? (
                <span className="flex items-center gap-2">
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none" role="status" aria-label={m.common_saving()}>
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25" />
                    <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
                  </svg>
                  {m.common_saving()}
                </span>
              ) : (
                editingId ? m.common_update() : m.common_create()
              )}
            </button>
            <button
              type="button"
              onClick={cancelForm}
              className="rounded-lg border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]"
            >
              {m.common_cancel()}
            </button>
          </div>
        </form>
      )}

      {/* Criteria list */}
      {criteriaQuery.isLoading ? (
        <div className="mt-6 space-y-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded-xl" />
          ))}
        </div>
      ) : criteria.length === 0 && !showForm ? (
        <div className="mt-12 text-center">
          <p className="text-[var(--text-muted)]">{m.jobs_search_criteria_no_results()}</p>
          {isFree ? (
            <Link
              to="/pricing"
              className="mt-2 inline-block text-sm font-medium text-[var(--accent)] hover:underline"
            >
              {m.settings_upgrade_pro()}
            </Link>
          ) : (
            <button
              onClick={() => setShowForm(true)}
              className="mt-2 text-sm font-medium text-[var(--text)] hover:underline"
            >
              {m.jobs_create_criteria_link()}
            </button>
          )}
        </div>
      ) : (
        <div className="mt-6 space-y-3">
          {criteria.map((c) => (
            <div key={c.id} className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 shadow-[var(--shadow-sm)]">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-semibold">{c.name}</h3>
                    <span className={`rounded px-2 py-0.5 text-[10px] font-medium ${
                      c.isActive ? "bg-status-success-bg text-status-success" : "bg-[var(--bg-secondary)] text-[var(--text-muted)]"
                    }`}>
                      {c.isActive ? m.common_active() : m.jobs_paused()}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-[var(--text-muted)]">
                    {c.role}
                    {c.location ? ` \u00B7 ${c.location}` : ""}
                    {c.salaryMin || c.salaryMax ? ` \u00B7 ${c.salaryMin ?? ""}${c.salaryMin && c.salaryMax ? "-" : ""}${c.salaryMax ?? ""} ${c.currency}` : ""}
                    {` \u00B7 ${c.workType}`}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {c.sources.map((s) => (
                      <span key={s} className="rounded bg-[var(--bg-secondary)] px-2 py-0.5 text-[10px] font-medium text-[var(--text-secondary)]">
                        {s}
                      </span>
                    ))}
                  </div>
                  {c.lastScrapedAt && (
                    <p className="mt-1 text-[10px] text-[var(--text-muted)]">
                      {m.jobs_last_scraped({ date: new Date(c.lastScrapedAt).toLocaleString() })}
                    </p>
                  )}
                </div>
              </div>

              {/* Alert toggles */}
              <div className="mt-3 flex items-center gap-4 border-t border-[var(--border)] pt-3">
                <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={c.alertInApp}
                    onChange={(e) => alertMutation.mutate({ criteriaId: c.id, inAppAlerts: e.target.checked })}
                    className="rounded border-[var(--border)]"
                  />
                  {m.jobs_in_app_alerts()}
                </label>
                <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)]">
                  <input
                    type="checkbox"
                    checked={c.alertEmail}
                    onChange={(e) => alertMutation.mutate({ criteriaId: c.id, emailAlerts: e.target.checked })}
                    className="rounded border-[var(--border)]"
                  />
                  {m.jobs_email_alerts()}
                </label>
              </div>

              {/* Actions */}
              <div className="mt-3 flex items-center gap-2">
                <button
                  onClick={() => toggleActiveMutation.mutate({ id: c.id, isActive: !c.isActive })}
                  className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]"
                >
                  {c.isActive ? m.jobs_pause() : m.jobs_resume()}
                </button>
                <button
                  onClick={() => scrapeMutation.mutate({ criteriaId: c.id })}
                  disabled={scrapeMutation.isPending}
                  className="rounded-lg bg-[var(--text)] px-3 py-1.5 text-xs font-medium text-[var(--text-inverse)] hover:opacity-90 disabled:opacity-50"
                >
                  {scrapeMutation.isPending ? m.jobs_scraping() : m.jobs_scrape_now()}
                </button>
                <button
                  onClick={() => startEdit(c)}
                  className="rounded-lg border border-[var(--border)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-raised)]"
                >
                  {m.jobs_edit()}
                </button>
                <button
                  onClick={() => {
                    if (confirm(m.jobs_confirm_delete_criteria())) {
                      deleteMutation.mutate({ id: c.id });
                    }
                  }}
                  className="rounded-lg px-3 py-1.5 text-xs font-medium text-status-error hover:bg-status-error-bg"
                >
                  {m.common_delete()}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
